import React, { useState, useEffect, useContext } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import logoImage from '../styles/images/logo.png';
// Import for Noto Sans Hebrew font
import '@fontsource/noto-sans-hebrew';
import { LanguageContext } from '../App';

const pages = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Contact' }
];

// Hebrew version of the navigation items
const hebrewPages = [
  { id: 'home', label: 'דף הבית' },
  { id: 'about', label: 'אודות' },
  { id: 'services', label: 'שירותים' },
  { id: 'portfolio', label: 'תיק עבודות' },
  { id: 'testimonials', label: 'המלצות' },
  { id: 'faq', label: 'שאלות נפוצות' },
  { id: 'contact', label: 'צור קשר' }
];

// Hebrew brand name styling
const hebrewFontStyle = {
  fontFamily: '"Noto Sans Hebrew", sans-serif',
  fontSize: '18px'
};

function Navbar() {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const { language, setLanguage, translations } = useContext(LanguageContext);

  // Switch document direction when language changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', language === 'he' ? 'rtl' : 'ltr');
    }
  }, [language]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  // Set mounted state after initial render
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    // Only add the listener if window is defined (client-side)
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      
      // Call once to set initial state
      handleScroll();
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [mounted]);

  const handleScrollToSection = (sectionId) => {
    if (typeof document !== 'undefined') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        handleCloseNavMenu();
      }
    }
  };

  // Add RTL detection
  useEffect(() => {
    if (mounted) {
      const htmlDir = document.documentElement.getAttribute('dir');
      setIsRtl(htmlDir === 'rtl');
    }
  }, [mounted]);

  // Use appropriate pages based on direction
  const navItems = translations[language].nav;

  if (!mounted) {
    // Return a simpler version without interactive elements during SSR/initial render
    return (
      <AppBar position="fixed" color="transparent" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography variant="h6" noWrap sx={hebrewFontStyle}>רמי פזהר - צלם</Typography>
          </Toolbar>
        </Container>
      </AppBar>
    );
  }

  return (
    <AppBar 
      position="fixed" 
      color={scrolled ? 'primary' : 'transparent'} 
      elevation={scrolled ? 4 : 0}
      sx={{ 
        transition: 'all 0.3s ease',
        bgcolor: scrolled ? 'primary.main' : 'transparent',
        pt: 'env(safe-area-inset-top)',
        backdropFilter: 'blur(10px)',
        backgroundColor: scrolled ? 'primary.main' : 'rgba(0,0,0,0.15)',
      }}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo and Brand */}
          <img 
            src={logoImage}
            alt="Rami Pazhar Logo" 
            style={{ 
              display: { xs: 'none', md: 'flex' }, 
              height: '40px',
              marginRight: language === 'he' ? 0 : 8, 
              marginLeft: language === 'he' ? 8 : 0 
            }} 
          />
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="#home"
            sx={{
              mr: language === 'he' ? 0 : 2,
              ml: language === 'he' ? 2 : 0,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              letterSpacing: '.1rem',
              color: 'white',
              textDecoration: 'none',
              ...hebrewFontStyle,
              fontSize: '24px'
            }}
          >
            {translations[language].websiteTitle}
          </Typography>

          {/* Navigation Items (Desktop) */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' }, 
            justifyContent: language === 'he' ? 'flex-start' : 'flex-end',
            alignItems: 'center'
          }}>
            {navItems.map((page) => (
              <Button
                key={page.id}
                onClick={() => handleScrollToSection(page.id)}
                sx={{ 
                  my: 2, 
                  color: 'white', 
                  display: 'block', 
                  mx: 1,
                  ...hebrewFontStyle
                }}
              >
                {page.label}
              </Button>
            ))}
            {/* Spacer to push language switcher to the end */}
            <Box sx={{ flexGrow: 1 }} />
            {/* Language Switcher at the end */}
            <Button
              sx={{ ml: language === 'he' ? 0 : 2, mr: language === 'he' ? 2 : 0, color: 'white', border: '1px solid white', borderRadius: '20px', fontWeight: 700 }}
              onClick={() => setLanguage(language === 'he' ? 'en' : 'he')}
            >
              {language === 'he' ? 'English' : 'עברית'}
            </Button>
          </Box>

          {/* Navigation Items (Mobile) */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'flex', md: 'none' },
            justifyContent: language === 'he' ? 'flex-end' : 'flex-start'
          }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: language === 'he' ? 'right' : 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: language === 'he' ? 'right' : 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {navItems.map((page) => (
                <MenuItem 
                  key={page.id} 
                  onClick={() => handleScrollToSection(page.id)}
                  sx={hebrewFontStyle}
                >
                  <Typography textAlign="center">{page.label}</Typography>
                </MenuItem>
              ))}
              {/* Language Switcher in mobile menu */}
              <MenuItem onClick={() => { setLanguage(language === 'he' ? 'en' : 'he'); handleCloseNavMenu(); }}>
                <Typography textAlign="center">{language === 'he' ? 'English' : 'עברית'}</Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Mobile Brand */}
          <Typography
            variant="h5"
            noWrap
            component="a"
            href="#home"
            sx={{
              mr: language === 'he' ? 0 : 2,
              ml: language === 'he' ? 2 : 0,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontWeight: 700,              
              letterSpacing: '.1rem',
              color: 'white',
              textDecoration: 'none',
              ...hebrewFontStyle
            }}
          >
            רמי פזהר - צלם
          </Typography>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar;
